// How the Reviews lens names a session and where it ran: its summary (read
// for the rows a listing shows, in bounded batches), the machine its findings
// carry and the person registered to it, through the endpoint label cache.
// Shared by the Triage and Investigate tabs, so both name a session the way
// the Sessions tab does.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import OverflowTooltip from "../OverflowTooltip.jsx";
import { rangeToTimeRange } from "../../lib/analyticsClient.js";
import { getSessionPromptPreviews } from "../../lib/sessionLabels.js";
import { dimensionMeta, scopeStepLabel, useLabels } from "../../lib/dimensionDefs.js";

// The machine a session ran on and the person registered to it, as the
// endpoint label cache names them (the sidebar's and the trace log's names);
// the short id until the name lands.
export function machineOf(endpointId) {
  if (!endpointId) return null;
  const name = scopeStepLabel({ dim: "endpoints", id: endpointId });
  const host = name && name !== endpointId ? name : `endpoint ${String(endpointId).slice(0, 8)}`;
  const user = dimensionMeta("endpoints", endpointId)?.registeredUser || null;
  return { host, user, text: user ? `${host}${" \u00b7 "}${user}` : host };
}

// A session's hover card: its summary, where it ran, its id.
export function SessionCard({ id, label, machine }) {
  return (
    <span className="grid gap-[2px]">
      <span>{label || "No summary was kept for this trace."}</span>
      {machine ? <span className="text-[color:var(--muted)]">{machine.text}</span> : null}
      <span className="font-mono text-[color:var(--muted)]">{id}</span>
    </span>
  );
}

// One session by its summary, the way the Sessions tab names it; the machine
// and the id on hover.
export function SessionName({ sessions, labels, fallback = null, machineFor }) {
  const cell = "w-[150px] shrink-0 truncate text-[11px] leading-[15px] text-[color:var(--muted)]";
  if (sessions.length !== 1) return <span className={cell}>{sessions.length.toLocaleString()} traces</span>;
  const id = String(sessions[0]).toLowerCase();
  const label = labels?.get(id) || fallback || null;
  return (
    <OverflowTooltip
      text={label || `Trace ${id.slice(0, 8)}`}
      always
      tooltipContent={<SessionCard id={id} label={label} machine={machineFor?.(id)} />}
      className={cell}
    />
  );
}

// A case's sessions in two lines: the one session's summary over the machine
// it ran on, or how many sessions over the machines they ran on.
export function CaseSessions({ c, labels, machineFor }) {
  const sessions = [...c.sessions].map((s) => String(s).toLowerCase());
  const machines = [...c.endpoints];
  const one = sessions.length === 1 ? sessions[0] : null;
  const label = one ? labels?.get(one) || c.sessionLabel || null : null;
  const where = machines.length === 1 ? machineOf(machines[0])?.text : machines.length > 1 ? `${machines.length.toLocaleString()} endpoints` : null;
  return (
    <span data-triage-sessions="" className="grid w-[170px] shrink-0 gap-[2px]">
      {one ? (
        <OverflowTooltip
          text={label || `Trace ${one.slice(0, 8)}`}
          always
          tooltipContent={<SessionCard id={one} label={label} machine={machineFor(one)} />}
          className="min-w-0 truncate text-[11px] leading-[1.15] text-[color:var(--muted)]"
        />
      ) : (
        <span className="truncate text-[11px] leading-[1.15] text-[color:var(--muted)]">{sessions.length.toLocaleString()} traces</span>
      )}
      {where ? <span data-triage-machine="" className="truncate text-[9px] leading-[1.2] text-[color:var(--muted-soft)]">{where}</span> : null}
    </span>
  );
}

// Session summaries for the sessions the listing shows, the way the Sessions
// tab names them: asked for in bounded batches, cached by the label module,
// kept as they land. A session with no summary is kept as known-unnamed.
const LABELLED_SESSIONS = 300;
export function useSessionNames(ids, window) {
  const [labels, setLabels] = useState(() => new Map());
  const asked = useRef(new Set());
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const key = ids.join(",");
  useEffect(() => {
    const want = ids.filter((id) => !asked.current.has(id)).slice(0, LABELLED_SESSIONS);
    if (!want.length || !window?.fromMs) return;
    for (const id of want) asked.current.add(id);
    const range = rangeToTimeRange({ from: new Date(window.fromMs).toISOString(), to: new Date(window.toMs).toISOString() });
    // A read the next one overtakes still lands: the ids it asked for are
    // not asked again, so its answer is theirs.
    Promise.resolve()
      .then(() => getSessionPromptPreviews(want, range))
      .then((previews) => {
        if (!mounted.current || !(previews instanceof Map)) return;
        setLabels((cur) => {
          const next = new Map(cur);
          for (const id of want) next.set(id, previews.get(id)?.label || null);
          return next;
        });
      }, () => { for (const id of want) asked.current.delete(id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, window?.fromMs, window?.toMs]);
  return labels;
}

// What a list calls the sessions its rows name, and where each ran: summaries
// read for them (a summary the Sessions tab already holds stands in while
// they land), and the machine each session's findings carry, named through
// the endpoint label cache in one subscription with the rows' own
// `endpoints`.
export function useSessionIdentity({ map, sessions, endpoints, window, sessionLabel, named = sessions }) {
  const read = useSessionNames(named, window);
  const labels = useMemo(() => ({ get: (id) => read.get(id) || sessionLabel?.(id) || null }), [read, sessionLabel]);
  const endpointOfSession = useMemo(() => {
    const out = new Map();
    for (const f of map.findings.values()) {
      const session = String(f.session).toLowerCase();
      if (f.endpoint && !out.has(session)) out.set(session, f.endpoint);
    }
    return out;
  }, [map]);
  const endpointSteps = useMemo(() => {
    const out = new Set(endpoints);
    for (const s of sessions) if (endpointOfSession.has(s)) out.add(endpointOfSession.get(s));
    return [...out].sort().map((id) => ({ dim: "endpoints", id }));
  }, [sessions, endpoints, endpointOfSession]);
  const tick = useLabels(endpointSteps);
  const machineFor = useCallback((session) => machineOf(endpointOfSession.get(String(session).toLowerCase())),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [endpointOfSession, tick]);
  return { labels, machineFor, tick };
}

